// src/pages/Payments.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiMoreHorizontal,
  FiHash,
  FiDollarSign,
  FiCalendar,
  FiCreditCard,
  FiRefreshCcw,
  FiUser,
  FiFileText,
} from 'react-icons/fi';

import api from '../api/axios';
import { listPayments /*, deletePayment */ } from '../services/paymentService';

// ✅ Money utils (avoid USD hardcode; default to company currency like Invoices.jsx)
import { formatMoney, safeCurrency, getCompanyCurrency } from '../utils/money';

/* ----------------------------- helpers ----------------------------- */

// ✅ Company-level prefs (timezone only) from localStorage
const COMPANY_PREFS = (() => {
  try {
    const raw =
      localStorage.getItem('companyPrefs') ||
      localStorage.getItem('company') ||
      localStorage.getItem('companyProfile');
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
})();

const COMPANY_TZ = COMPANY_PREFS?.timezone || COMPANY_PREFS?.timeZone || 'UTC';

// ✅ Shared money helpers (company-currency default; NO USD hardcode)
const fmtMoney = (n, c) => formatMoney(n, safeCurrency(c || getCompanyCurrency()));

// ✅ Date formatting respects company timezone
const fmtDateOnlyUTC = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: COMPANY_TZ,
      })
    : '—';

const resolveId = (row) => row?.paymentId || row?._id || row?.id;

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Disputed', value: 'disputed' },
  { label: 'Canceled', value: 'canceled' },
];

const METHOD_OPTIONS = [
  { label: 'Card', value: 'card' },
  { label: 'Cash', value: 'cash' },
  { label: 'Cheque', value: 'cheque' },
  { label: 'Bank Transfer', value: 'bank_transfer' },
  { label: 'E-transfer', value: 'e_transfer' },
  { label: 'Other', value: 'other' },
];

const SOURCE_OPTIONS = [
  { label: 'Manual', value: 'manual' },
  { label: 'Stripe', value: 'stripe' },
];

const STATUS_CHIP = {
  pending: 'bg-amber-100 text-amber-700 border border-amber-200',
  succeeded: 'bg-green-100 text-green-700 border border-green-200',
  failed: 'bg-rose-100 text-rose-700 border border-rose-200',
  refunded: 'bg-blue-100 text-blue-700 border border-blue-200',
  disputed: 'bg-orange-100 text-orange-700 border border-orange-200',
  canceled: 'bg-slate-100 text-slate-700 border border-slate-200',
};

const SOURCE_CHIP = {
  manual: 'bg-slate-100 text-slate-800 border border-slate-200',
  stripe: 'bg-purple-100 text-purple-800 border border-purple-200',
};

const SORTABLE = [
  { id: 'paymentDate', label: 'Payment Date' },
  { id: 'createdAt', label: 'Created' },
  { id: 'amount', label: 'Amount' },
  { id: 'netAmount', label: 'Net Amount' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/* ----------------------------- small portal menu ----------------------------- */

function ActionMenuPortal({
  anchorEl,
  row,
  canMutate,
  onClose,
  onViewPayment,
  onViewInvoice,
  onViewClient,
  onOpenInvoicePdf,
  // onDeletePayment, // keep for future if needed
}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, origin: 'top-right' });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const vh = window.innerHeight;

    const menuW = 240;
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);

    let top = r.bottom + gap;
    let origin = 'top-right';
    setStyle((s) => ({ ...s, top, left, origin }));

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      setStyle({ top, left, origin });
    });
  }, [anchorEl]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onScrollOrResize = () => onClose();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  const paymentId = resolveId(row);
  const invoiceId = row?.invoiceId || row?.invoice?._id || row?.invoice?.id;
  const clientId = row?.clientId || row?.client?._id || row?.client?.id;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: style.top, left: style.left, zIndex: 50 }}
      className={`min-w-[240px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${style.origin} transform transition duration-100`}
    >
      {paymentId && (
        <button
          role="menuitem"
          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
          onClick={() => {
            onClose();
            onViewPayment(paymentId);
          }}
        >
          <FiHash /> View payment
        </button>
      )}

      {invoiceId && (
        <button
          role="menuitem"
          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
          onClick={() => {
            onClose();
            onViewInvoice(invoiceId);
          }}
        >
          <FiFileText /> View invoice
        </button>
      )}

      {clientId && (
        <button
          role="menuitem"
          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
          onClick={() => {
            onClose();
            onViewClient(clientId);
          }}
        >
          <FiUser /> View client
        </button>
      )}

      {invoiceId && (
        <button
          role="menuitem"
          className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
          onClick={async () => {
            onClose();
            await onOpenInvoicePdf(invoiceId);
          }}
        >
          <FiFileText /> Open invoice PDF
        </button>
      )}

      {/* Reserved for future destructive actions */}
      {/*
      <div className="my-1 h-px bg-slate-100" />
      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left text-rose-600 ${
          canMutate ? 'hover:bg-rose-50' : 'opacity-40 cursor-not-allowed'
        }`}
        onClick={() => {
          onClose();
          onDeletePayment(paymentId);
        }}
      >
        Delete payment
      </button>
      */}
    </div>,
    document.body
  );
}

/* ----------------------------- main ----------------------------- */

const Payments = () => {
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // ✅ RBAC: default locked down until profile resolves; never trust localStorage for permissions
  const [role, setRole] = useState('viewer');
  const [profileLoaded, setProfileLoaded] = useState(false);
  const canMutate =
    profileLoaded && ['owner', 'admin', 'staff'].includes(String(role || '').toLowerCase());

  // Query state
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [method, setMethod] = useState('all');
  const [source, setSource] = useState('all');
  const [dateField, setDateField] = useState('paymentDate'); // for future-proofing
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('paymentDate');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Data
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 25 });
  const [loading, setLoading] = useState(true);

  // UI state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  // ✅ single source of truth for list page currency (like Invoices.jsx)
  const companyCurrency = getCompanyCurrency();

  const resetFilters = () => {
    setSearch('');
    setStatus('all');
    setMethod('all');
    setSource('all');
    setDateField('paymentDate');
    setDateFrom('');
    setDateTo('');
    setSortBy('paymentDate');
    setSortDir('desc');
    setLimit(25);
    setPage(1);
  };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get('/auth/profile');
      const r = res?.data?.data || res?.data;
      setRole(r?.role || 'viewer');
      localStorage.setItem('profile', JSON.stringify(r || {}));
    } catch {
      setRole('viewer');
    } finally {
      setProfileLoaded(true);
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search.trim() || undefined,
        status: status === 'all' ? undefined : status,
        method: method === 'all' ? undefined : method,
        source: source === 'all' ? undefined : source,
        dateField,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        sortBy,
        sortOrder: sortDir,
      };

      const { items, meta: m } = await listPayments(params);

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[Payments] list payload (service):', { items, meta: m });
      }

      setRows(Array.isArray(items) ? items : []);

      setMeta({
        page: Number(m?.page ?? 1),
        limit: Number(m?.limit ?? limit),
        total: Number.isFinite(Number(m?.total))
          ? Number(m.total)
          : Array.isArray(items)
          ? items.length
          : 0,
        totalPages:
          Number.isFinite(Number(m?.totalPages)) && Number(m.totalPages) > 0
            ? Number(m.totalPages)
            : Number.isFinite(Number(m?.total)) && Number(m.total) > 0
            ? Math.ceil(Number(m.total) / Number(m?.limit ?? limit ?? 25))
            : 1,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to load payments');
      setRows([]);
      setMeta({ page: 1, totalPages: 1, total: 0, limit });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, method, source, dateField, dateFrom, dateTo, sortBy, sortDir]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  /* ----------------------------- actions ----------------------------- */

  const openInvoicePdf = useCallback(async (invoiceId) => {
    try {
      if (!invoiceId) throw new Error('Missing invoice id');

      const resp = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
        params: { download: 1, v: Date.now() },
      });

      const blob = resp?.data;
      const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();

      if (ct.includes('application/json')) {
        const text = await blob.text?.();
        try {
          const json = JSON.parse(text || '{}');
          throw new Error(json?.error || 'Server error while generating PDF');
        } catch {
          throw new Error(text || 'Server error while generating PDF');
        }
      }

      if (!(blob instanceof Blob) || blob.size === 0) throw new Error('Empty PDF response');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      const msg = e?.message || 'Unable to download invoice PDF';
      toast.error(`PDF error: ${msg}`);
    }
  }, []);

  // Optional delete flow (disabled in UI by default for now)
  const doDeletePayment = async (id) => {
    if (!canMutate) return;
    if (!window.confirm('Delete this payment? This will update the linked invoice balance.')) return;
    try {
      // await deletePayment(id);
      toast.success('Payment deleted');
      fetchPayments();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to delete payment');
    }
  };

  /* ----------------------------- derived ----------------------------- */

  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    return (
      <button
        type="button"
        onClick={() => {
          if (sortBy === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortBy(id);
            setSortDir('asc');
          }
          setPage(1);
        }}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {label}
        {active ? (sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />) : null}
      </button>
    );
  };

  const statusCounts = useMemo(() => {
    const c = { pending: 0, succeeded: 0, failed: 0, refunded: 0, disputed: 0, canceled: 0 };
    for (const r of rows) {
      const s = String(r.status || '').toLowerCase();
      if (c[s] !== undefined) c[s]++;
    }
    return c;
  }, [rows]);

  const sourceCounts = useMemo(() => {
    const c = { manual: 0, stripe: 0 };
    for (const r of rows) {
      const s = String(r.source || '').toLowerCase();
      if (c[s] !== undefined) c[s]++;
    }
    return c;
  }, [rows]);

  const pageTotalAmount = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.amount || 0), 0),
    [rows]
  );
  const pageTotalNet = useMemo(
    () => rows.reduce((sum, r) => sum + Number(r.netAmount || 0), 0),
    [rows]
  );

  const computed = useMemo(() => {
    const t = Number(meta?.total ?? 0);
    const l = Number(meta?.limit ?? limit ?? 25);
    const p = Number(meta?.page ?? page ?? 1);
    const tp = Number(meta?.totalPages);
    const totalPages =
      Number.isFinite(tp) && tp > 0 ? tp : t > 0 && l > 0 ? Math.ceil(t / l) : 1;
    return { total: t, limit: l, page: p, totalPages };
  }, [meta.total, meta.limit, meta.page, meta.totalPages, limit, page]);

  /* ----------------------------- UI ----------------------------- */

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      {/* Header (mirrors Invoices) */}
      <div className="rounded-2xl mb-6 border border-slate-200 bg-white shadow-sm">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                  <FiCreditCard />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Payments</h1>
              </div>
              <p className="text-slate-600 mt-1">
                See all incoming payments across invoices and Stripe/manual sources.
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiDollarSign /> Page Total (gross)
              </div>
              <div className="text-lg font-bold text-slate-900">
                {fmtMoney(pageTotalAmount, companyCurrency)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs flex items-center gap-1">
                <FiDollarSign /> Page Total (net)
              </div>
              <div className="text-lg font-bold text-slate-900">
                {pageTotalNet > 0 ? fmtMoney(pageTotalNet, companyCurrency) : '—'}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs">Stripe</div>
              <div className="text-lg font-bold text-slate-900">{sourceCounts.stripe || 0}</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-slate-500 text-xs">Succeeded</div>
              <div className="text-lg font-bold text-slate-900">{statusCounts.succeeded || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="mb-4">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="relative w-full sm:w-96">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search payment #, client, invoice, reference…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            <div className="flex flex-1 sm:flex-none gap-2 justify-stretch sm:justify-end flex-wrap">
              {/* Status */}
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiFilter />
                </div>
                <select
                  className="pl-9 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                  title="Filter by status"
                >
                  <option value="all">All Statuses</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Method */}
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiFilter />
                </div>
                <select
                  className="pl-9 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={method}
                  onChange={(e) => {
                    setMethod(e.target.value);
                    setPage(1);
                  }}
                  title="Filter by method"
                >
                  <option value="all">All Methods</option>
                  {METHOD_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Source */}
              <div className="relative">
                <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <FiFilter />
                </div>
                <select
                  className="pl-9 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value);
                    setPage(1);
                  }}
                  title="Filter by source"
                >
                  <option value="all">All Sources</option>
                  {SOURCE_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              {/* Date field + range */}
              <div className="flex gap-2">
                <select
                  className="px-3 py-2 text-sm border rounded-xl bg-white"
                  value={dateField}
                  onChange={(e) => {
                    setDateField(e.target.value);
                    setPage(1);
                  }}
                  title="Date field"
                >
                  <option value="paymentDate">Payment</option>
                  <option value="createdAt">Created</option>
                </select>
                <input
                  type="date"
                  className="px-3 py-2 text-sm border rounded-xl bg-white"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  title="From"
                />
                <input
                  type="date"
                  className="px-3 py-2 text-sm border rounded-xl bg-white"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  title="To"
                />
              </div>

              {/* Sort / size */}
              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  title="Sort by"
                >
                  {SORTABLE.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={sortDir}
                  onChange={(e) => {
                    setSortDir(e.target.value);
                    setPage(1);
                  }}
                  title="Sort direction"
                >
                  <option value="desc">Newest</option>
                  <option value="asc">Oldest</option>
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <div className="relative">
                <select
                  className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  title="Rows per page"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>

              <button
                className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl bg-white hover:bg-slate-50"
                onClick={resetFilters}
                title="Reset filters"
              >
                <FiRefreshCcw /> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="paymentDate" label="Payment Date" />
                </th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="createdAt" label="Created" />
                </th>
                <th className="px-4 py-3 text-left font-bold">Payment #</th>
                <th className="px-4 py-3 text-left font-bold">Client</th>
                <th className="px-4 py-3 text-left font-bold">Invoice</th>
                <th className="px-4 py-3 text-left font-bold">Method</th>
                <th className="px-4 py-3 text-left font-bold">Source</th>
                <th className="px-4 py-3 text-left font-bold">Status</th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="amount" label="Amount" />
                </th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="netAmount" label="Net" />
                </th>
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={11} className="px-4 py-4">
                      <div className="h-4 w-full rounded bg-slate-100 animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-14">
                    <div className="text-center">
                      <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <FiCreditCard className="text-slate-400" />
                      </div>
                      <div className="text-slate-600 font-semibold">No payments found</div>
                      <div className="text-slate-500 text-sm">
                        Adjust filters or record a payment from an invoice.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const id = resolveId(row);
                  const clientId = row?.clientId || row?.client?._id || row?.client?.id;
                  const invoiceId = row?.invoiceId || row?.invoice?._id || row?.invoice?.id;

                  const clientName =
                    row.clientName || row?.client?.name || row?.client?.displayName || '—';

                  const invoiceNumber = row.invoiceNumber || row?.invoice?.invoiceNumber || '—';

                  // ✅ currency fallback: row.currency -> company currency (no USD hardcode)
                  const currency = safeCurrency(row.currency || companyCurrency);
                  const statusKey = String(row.status || '').toLowerCase();
                  const sourceKey = String(row.source || '').toLowerCase();

                  return (
                    <tr key={id} className="group transition hover:bg-blue-50/40">
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDateOnlyUTC(row.paymentDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDateOnlyUTC(row.createdAt)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {id ? (
                          <button
                            className="font-semibold text-slate-900 hover:underline inline-flex items-center gap-1"
                            onClick={() => navigate(`/payments/${id}`)}
                            title="Open payment"
                          >
                            <FiHash className="text-slate-500" />
                            {row.paymentNumber || '—'}
                          </button>
                        ) : (
                          <span className="font-semibold text-slate-900">{row.paymentNumber || '—'}</span>
                        )}
                        <div className="text-xs text-slate-500">{currency}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {clientId ? (
                          <button
                            className="text-slate-900 font-medium hover:underline"
                            onClick={() => navigate(`/clients/${clientId}`)}
                            title="Open client"
                          >
                            {clientName}
                          </button>
                        ) : (
                          <span className="text-slate-700">{clientName}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {invoiceId ? (
                          <button
                            className="font-medium text-blue-700 hover:underline"
                            onClick={() => navigate(`/invoices/${invoiceId}`)}
                            title="Open invoice"
                          >
                            {invoiceNumber}
                          </button>
                        ) : (
                          <span className="text-slate-700">{invoiceNumber}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {row.method
                          ? {
                              card: 'Card',
                              cash: 'Cash',
                              cheque: 'Cheque',
                              bank_transfer: 'Bank Transfer',
                              e_transfer: 'E-transfer',
                              other: 'Other',
                            }[row.method] || row.method
                          : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            SOURCE_CHIP[sourceKey] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.source ? row.source.charAt(0).toUpperCase() + row.source.slice(1) : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_CHIP[statusKey] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {fmtMoney(row.amount, currency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {row.netAmount != null && row.netAmount >= 0 ? fmtMoney(row.netAmount, currency) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex justify-end">
                          <button
                            className="p-2 rounded hover:bg-slate-100"
                            onClick={(e) => {
                              setMenuRow(row);
                              setMenuAnchor(e.currentTarget);
                            }}
                            aria-haspopup="menu"
                            aria-expanded={menuRow && resolveId(menuRow) === id ? 'true' : 'false'}
                            title="Actions"
                          >
                            <FiMoreHorizontal />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No payments found.</div>
          ) : (
            rows.map((row) => {
              const id = resolveId(row);
              const clientId = row?.clientId || row?.client?._id || row?.client?.id;
              const invoiceId = row?.invoiceId || row?.invoice?._id || row?.invoice?.id;

              const clientName = row.clientName || row?.client?.name || row?.client?.displayName || '—';
              const invoiceNumber = row.invoiceNumber || row?.invoice?.invoiceNumber || '—';

              const currency = safeCurrency(row.currency || companyCurrency);
              const statusKey = String(row.status || '').toLowerCase();
              const sourceKey = String(row.source || '').toLowerCase();

              return (
                <div key={id} className="p-4 hover:bg-blue-50/60 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 flex items-center gap-1">
                        <FiCreditCard className="text-slate-500" />
                        {fmtMoney(row.amount, currency)}
                      </div>
                      <div className="text-sm text-slate-600 flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> {fmtDateOnlyUTC(row.paymentDate)}
                        </span>
                        {invoiceNumber && <span className="inline-flex items-center gap-1">• #{invoiceNumber}</span>}
                      </div>
                      <div className="mt-1 text-sm text-slate-700">
                        {clientId ? (
                          <button
                            className="font-medium hover:underline"
                            onClick={() => navigate(`/clients/${clientId}`)}
                          >
                            {clientName}
                          </button>
                        ) : (
                          clientName
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_CHIP[statusKey] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : '—'}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            SOURCE_CHIP[sourceKey] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.source ? row.source.charAt(0).toUpperCase() + row.source.slice(1) : '—'}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">{currency}</div>
                    </div>

                    <div className="text-right">
                      <div className="font-semibold text-sm">{row.paymentNumber || '—'}</div>
                      {id && (
                        <button
                          className="mt-1 text-xs text-blue-700 hover:underline"
                          onClick={() => navigate(`/payments/${id}`)}
                        >
                          View payment
                        </button>
                      )}
                      {invoiceId && (
                        <button
                          className="mt-1 text-xs text-blue-700 hover:underline block"
                          onClick={() => navigate(`/invoices/${invoiceId}`)}
                        >
                          View invoice
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {id && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={() => navigate(`/payments/${id}`)}
                      >
                        <FiHash className="inline -mt-0.5 mr-1" />
                        View Payment
                      </button>
                    )}

                    {invoiceId && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={() => navigate(`/invoices/${invoiceId}`)}
                      >
                        View Invoice
                      </button>
                    )}

                    {invoiceId && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={async () => {
                          await openInvoicePdf(invoiceId);
                        }}
                      >
                        <FiFileText className="inline -mt-0.5 mr-1" />
                        Invoice PDF
                      </button>
                    )}

                    {clientId && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm"
                        onClick={() => navigate(`/clients/${clientId}`)}
                      >
                        View Client
                      </button>
                    )}

                    {/* Delete kept for future */}
                    {/* {canMutate && (
                      <button
                        className="px-3 py-1.5 rounded-xl border text-sm text-rose-600 border-rose-200"
                        onClick={() => doDeletePayment(id)}
                      >
                        Delete
                      </button>
                    )} */}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Page <span className="font-semibold">{computed.page}</span> of{' '}
            <span className="font-semibold">{computed.totalPages}</span> •{' '}
            <span className="font-semibold">{computed.total}</span> total
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={computed.page <= 1 || loading}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700">
              Page <span className="font-semibold">{computed.page}</span> of{' '}
              <span className="font-semibold">{computed.totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(computed.totalPages || 1, p + 1))}
              disabled={computed.page >= (computed.totalPages || 1) || loading}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Portaled action menu */}
      {menuAnchor && menuRow && (
        <ActionMenuPortal
          anchorEl={menuAnchor}
          row={menuRow}
          canMutate={canMutate}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          onViewPayment={(paymentId) => navigate(`/payments/${paymentId}`)}
          onViewInvoice={(invoiceId) => navigate(`/invoices/${invoiceId}`)}
          onViewClient={(clientId) => navigate(`/clients/${clientId}`)}
          onOpenInvoicePdf={openInvoicePdf}
          // onDeletePayment={doDeletePayment}
        />
      )}
    </div>
  );
};

export default Payments;
